import type { ResolvedBundleItem } from './types';

// Client-facing vocabulary for a bundle hub card (docs/script-bundles-plan.md
// §5, mockups/script-bundles.html §03). Distinct from STATUS_LABELS in
// types.ts, which is the terse admin-facing status word — clients get a
// warmer sentence that tells them what to actually do next.

export type BundleChipTone = 'success' | 'accent' | 'tint' | 'neutral';

export interface BundleChip {
  label: string;
  tone: BundleChipTone;
}

/**
 * Maps one resolved bundle item to its hub chip. Approved always wins and
 * reads "✓ Approved"; a script with feedback already sent (and not yet
 * approved) reads "Feedback sent, with Henry" so the client knows the ball
 * is in his court, not theirs; any other ready script is "Ready for your
 * review" — the one card that should draw the eye. Coming-soon items (not-
 * yet-ready scripts or placeholders) always read "In the works".
 */
export function bundleItemChip(item: ResolvedBundleItem): BundleChip {
  if (item.kind === 'coming_soon') {
    return { label: 'In the works', tone: 'neutral' };
  }
  if (item.status === 'approved') {
    return { label: '✓ Approved', tone: 'success' };
  }
  if (item.feedback_submitted_at !== null) {
    return { label: 'Feedback sent, with Henry', tone: 'tint' };
  }
  return { label: 'Ready for your review', tone: 'accent' };
}

export interface BundleProgress {
  /** Scripts the client can actually open — coming-soon items excluded. */
  readyCount: number;
  approvedCount: number;
  comingSoonCount: number;
  /** True once every ready script is approved, and there's at least one —
   *  independent of whether more scripts are still coming. Drives the 100%
   *  progress-bar fill even mid-staged-delivery. */
  allApproved: boolean;
  /** True only when the bundle is genuinely finished: every ready script is
   *  approved AND nothing is still coming. Drives the full celebration
   *  banner + confetti + the once-ever localStorage mark — allApproved alone
   *  would fire those prematurely while a staged-delivery bundle still has
   *  "N more on the way" sitting right next to the banner. */
  celebrate: boolean;
}

/**
 * Aggregate hub progress, computed client-side from the already-filtered
 * items the bundle API returns. Mirrors the mockup's rule: the denominator
 * is reviewable (ready) scripts only — coming-soon items are acknowledged
 * separately ("N more on the way") rather than counted against the fraction.
 */
export function computeBundleProgress(items: ResolvedBundleItem[]): BundleProgress {
  let readyCount = 0;
  let approvedCount = 0;
  let comingSoonCount = 0;
  for (const item of items) {
    if (item.kind === 'coming_soon') {
      comingSoonCount += 1;
      continue;
    }
    readyCount += 1;
    if (item.status === 'approved') approvedCount += 1;
  }
  const allApproved = readyCount > 0 && approvedCount === readyCount;
  return {
    readyCount,
    approvedCount,
    comingSoonCount,
    allApproved,
    celebrate: allApproved && comingSoonCount === 0,
  };
}
